import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SectionTitle } from './PageShell';
import { AdminGuard } from './AdminPages';
import AdminShell from './AdminShell';
import { api } from '../lib/api';
import { Search, Loader2, CheckCircle2, XCircle, KeyRound, Trash2, RefreshCw, Filter, GraduationCap, UserCheck, Shield, Copy, Building2, X } from 'lucide-react';

const ROLE_OPTIONS = [
  { v: '', l: 'Tous les rôles' },
  { v: 'eleve', l: 'Élève' },
  { v: 'enseignant', l: 'Enseignant' },
];
const STATUS_OPTIONS = [
  { v: '', l: 'Tous statuts' },
  { v: 'pending', l: 'En attente' },
  { v: 'active', l: 'Actif' },
  { v: 'disabled', l: 'Désactivé' },
];
const ROLE_ICON = { eleve: GraduationCap, enseignant: UserCheck };
const ROLE_LABEL = { eleve: 'Élève', enseignant: 'Enseignant' };

function StatusBadge({ s }) {
  const map = {
    active: { c: 'bg-emerald-500/15 text-emerald-300 border-emerald-700/40', l: 'Actif' },
    pending: { c: 'bg-amber-500/15 text-amber-300 border-amber-700/40', l: 'En attente' },
    disabled: { c: 'bg-red-500/15 text-red-300 border-red-700/40', l: 'Désactivé' },
  }[s] || { c: 'bg-white/5 text-white/50 border-white/10', l: s };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10.5px] font-bold uppercase tracking-wider ${map.c}`}>{map.l}</span>;
}

function ResetPasswordModal({ tempPwd, onClose }) {
  if (!tempPwd) return null;
  const copy = () => navigator.clipboard?.writeText(tempPwd);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="reset-pwd-modal">
      <div className="rounded-2xl border border-red-700/40 bg-[#0d0d0e] p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2 font-mono-spec">Mot de passe temporaire</div>
        <h3 className="font-serif-editorial text-white text-[22px] mb-3">Réinitialisation effectuée</h3>
        <p className="text-white/60 text-[13.5px] mb-4">Communique ce mot de passe à l'utilisateur. Il pourra le changer après connexion.</p>
        <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10 mb-5">
          <code className="flex-1 text-white font-mono-spec text-[15px]" data-testid="temp-password-value">{tempPwd}</code>
          <button onClick={copy} className="w-9 h-9 rounded-md border border-white/10 hover:bg-white/10 inline-flex items-center justify-center text-white/70"><Copy size={14} /></button>
        </div>
        <button onClick={onClose} className="w-full h-11 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] font-semibold">Fermer</button>
      </div>
    </div>
  );
}

function CreateSecretaireModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  if (!open) return null;
  const save = async () => {
    setSaving(true); setErr('');
    try {
      await api.adminCreateSecretaire(form);
      onCreated(form); setForm({ name: '', email: '', password: '' });
    } catch (e) { setErr(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };
  const cls = 'w-full h-11 rounded-lg bg-white/5 border border-white/15 px-3 text-white text-[13.5px] outline-none focus:border-amber-500';
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="create-secretaire-modal">
      <div className="rounded-2xl border border-amber-700/40 bg-[#0d0d0e] p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400 font-bold font-mono-spec">Secrétariat / Gérant</div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={16} /></button>
        </div>
        <h3 className="font-serif-editorial text-white text-[22px] mb-2">Créer un compte secrétariat</h3>
        <p className="text-white/55 text-[13px] mb-4">Ce compte accède à l'outil de gestion : planning, fiches élèves/enseignants et export DSR (<code className="text-amber-300">/secretariat</code>).</p>
        <div className="space-y-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom et prénom *" data-testid="secretaire-name-input" className={cls} />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email de connexion *" data-testid="secretaire-email-input" className={cls} />
          <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} type="text" placeholder="Mot de passe (8 caractères min) *" data-testid="secretaire-password-input" className={cls} />
          {err && <div className="text-red-300 text-[12.5px]">⚠️ {String(err)}</div>}
          <button onClick={save} disabled={saving || form.name.trim().length < 2 || !form.email || form.password.length < 8} data-testid="secretaire-create-submit" className="w-full h-11 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black text-[14px] font-bold inline-flex items-center justify-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />} Créer le compte
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsersPage() {
  const [params, setParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tempPwd, setTempPwd] = useState(null);
  const [showSecModal, setShowSecModal] = useState(false);

  const role = params.get('role') || '';
  const statusF = params.get('status') || '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {};
      if (role) q.role = role;
      if (statusF) q.status_filter = statusF;
      if (search) q.search = search;
      const data = await api.adminListUsers(q);
      setUsers(data);
    } finally { setLoading(false); }
  }, [role, statusF, search]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };

  const approve = async (id) => { await api.adminApproveUser(id); load(); };
  const disable = async (id) => {
    if (!window.confirm('Désactiver ce compte ? L\'utilisateur ne pourra plus se connecter.')) return;
    await api.adminDisableUser(id); load();
  };
  const enable = async (id) => { await api.adminEnableUser(id); load(); };
  const del = async (id) => {
    if (!window.confirm('Supprimer définitivement ce compte et toutes ses données livret ?')) return;
    await api.adminDeleteUser(id); load();
  };
  const reset = async (id) => {
    if (!window.confirm('Réinitialiser le mot de passe ? L\'ancien sera invalidé.')) return;
    const r = await api.adminResetPassword(id);
    setTempPwd(r.temporary_password);
  };

  const toggleSecAccess = async (u) => {
    const grant = !u.secretariat_access;
    if (!window.confirm(grant ? `Donner à ${u.name} l'accès au back-office Secrétariat / gérant (planning, fiches, export DSR) ?` : `Retirer l'accès secrétariat de ${u.name} ?`)) return;
    await api.adminUpdateUser(u.id, { secretariat_access: grant }).catch(() => {});
    load();
  };

  const onSubmit = (e) => { e.preventDefault(); load(); };

  return (
    <AdminGuard>
      <AdminShell>
        <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-10" data-testid="admin-users-page">
          <SectionTitle
            kicker={`Back-office · ${users.length} utilisateur${users.length > 1 ? 's' : ''}`}
            title="Gestion des utilisateurs"
            sub="Valider, désactiver, réinitialiser ou supprimer les comptes Élève, Enseignant et Futur enseignant."
          />

          <div className="flex justify-end mb-4">
            <button onClick={() => setShowSecModal(true)} data-testid="open-create-secretaire-btn" className="h-10 px-5 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-[13px] font-bold inline-flex items-center gap-2">
              <Building2 size={14} /> + Compte secrétariat / gérant
            </button>
          </div>

          {/* Filters */}
          <form onSubmit={onSubmit} className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="admin-users-filters">
            <div className="inline-flex items-center gap-2 text-white/40 text-[11.5px] uppercase tracking-wider font-mono-spec">
              <Filter size={12} /> Filtres
            </div>
            <div className="flex-1 min-w-[200px] relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="admin-users-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par email ou nom…"
                className="w-full h-10 pl-9 pr-3 rounded-lg bg-white/5 border border-white/15 text-white text-[13px] outline-none focus:border-red-500"
              />
            </div>
            <select data-testid="admin-users-role-filter" value={role} onChange={(e) => setFilter('role', e.target.value)} className="h-10 px-3 rounded-lg bg-white/5 border border-white/15 text-white text-[13px] outline-none focus:border-red-500">
              {ROLE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            <select data-testid="admin-users-status-filter" value={statusF} onChange={(e) => setFilter('status', e.target.value)} className="h-10 px-3 rounded-lg bg-white/5 border border-white/15 text-white text-[13px] outline-none focus:border-red-500">
              {STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            <button type="button" onClick={load} className="h-10 w-10 rounded-lg border border-white/10 text-white/70 hover:bg-white/10 inline-flex items-center justify-center" title="Recharger"><RefreshCw size={14} /></button>
          </form>

          {/* Table */}
          {loading ? (
            <div className="text-white/50 inline-flex items-center gap-2 py-10"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden" data-testid="admin-users-table">
              <table className="w-full text-[13px]">
                <thead className="bg-white/[0.03] text-white/50 text-[11px] uppercase tracking-wider font-mono-spec">
                  <tr>
                    <th className="text-left p-4">Utilisateur</th>
                    <th className="text-left p-4 hidden md:table-cell">Rôle</th>
                    <th className="text-left p-4 hidden md:table-cell">Statut</th>
                    <th className="text-left p-4 hidden lg:table-cell">Créé le</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map((u) => {
                    const Icon = ROLE_ICON[u.role] || GraduationCap;
                    return (
                      <tr key={u.id} className="hover:bg-white/[0.02]" data-testid={`admin-users-row-${u.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-red-600/10 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0">
                              {u.is_admin ? <Shield size={14} /> : <Icon size={14} />}
                            </div>
                            <div className="min-w-0">
                              <div className="text-white font-semibold inline-flex items-center gap-2">
                                {u.name}
                                {u.is_admin && <span className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[9.5px] font-bold uppercase">ADMIN</span>}
                              </div>
                              <div className="text-white/45 text-[11.5px] truncate font-mono-spec">{u.email}</div>
                              {u.teacher_code && <div className="text-red-400/80 text-[11px] font-mono-spec mt-0.5">Code prof : {u.teacher_code}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="p-4 hidden md:table-cell text-white/75">{ROLE_LABEL[u.role] || u.role}</td>
                        <td className="p-4 hidden md:table-cell"><StatusBadge s={u.status} /></td>
                        <td className="p-4 hidden lg:table-cell text-white/50 text-[12px] font-mono-spec">{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                        <td className="p-4 text-right">
                          <div className="inline-flex gap-1 flex-wrap justify-end">
                            {u.status === 'pending' && (
                              <button data-testid={`approve-${u.id}`} onClick={() => approve(u.id)} className="h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-[11.5px] font-bold inline-flex items-center gap-1"><CheckCircle2 size={12} /> Valider</button>
                            )}
                            {u.status === 'disabled' && (
                              <button data-testid={`enable-${u.id}`} onClick={() => enable(u.id)} className="h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-[11.5px] font-bold inline-flex items-center gap-1"><CheckCircle2 size={12} /> Réactiver</button>
                            )}
                            {!u.is_admin && u.status === 'active' && (
                              <button data-testid={`disable-${u.id}`} onClick={() => disable(u.id)} className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-amber-700/30 text-amber-400 hover:bg-amber-950/30" title="Désactiver"><XCircle size={13} /></button>
                            )}
                            <button data-testid={`reset-pwd-${u.id}`} onClick={() => reset(u.id)} className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-white/10 text-white/70 hover:text-white hover:border-white/30" title="Reset mot de passe"><KeyRound size={13} /></button>
                            {!u.is_admin && (
                              <button data-testid={`delete-${u.id}`} onClick={() => del(u.id)} className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-red-700/30 text-red-400 hover:bg-red-950/30" title="Supprimer"><Trash2 size={13} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {users.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-white/45">Aucun utilisateur ne correspond à ces filtres.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <ResetPasswordModal tempPwd={tempPwd} onClose={() => setTempPwd(null)} />
        <CreateSecretaireModal open={showSecModal} onClose={() => setShowSecModal(false)} onCreated={() => { setShowSecModal(false); load(); }} />
      </AdminShell>
    </AdminGuard>
  );
}
